<template>
  <div v-click-outside="clickedOutside" v-element-loader="{ endpoint: 'product/search' }" class="article-search">
    <oie-base-input
      id="header-article"
      v-model="searchText"
      :label-text="tStatic('labelArticleSearch')"
      :placeholder="tStatic('quickOrderSearchPlaceholder')"
      :use-gray-input="useGrayInput"
      data-pw="article-search-itemid-input"
      @keydown="onSearchKeyDown"
      @focus="toggleSearchResult(true)">
    </oie-base-input>

    <oie-base-input
      id="header-quantity"
      v-model="productQuantity"
      class="article-search__amount"
      data-pw="article-search-quantity-input"
      :use-gray-input="useGrayInput"
      @keydown.enter="addProduct()">
      <template v-slot:iconAppend>
        <button class="article-search__search-action" data-pw="article-search-add" @click="addProduct()">
          <oie-use-svg svg="close" class="svg--45 article-search__search-svg"></oie-use-svg>
        </button>
      </template>
    </oie-base-input>

    <div v-if="hasResults && showSearchResults" class="article-search-results" :class="{ 'article-search-results--has-hits': hasResults }">
      <div
        v-for="(product,i) in searchResult"
        :key="`auto-result-${i}`"
        :class="['article-search-result', { 'article-search-result--is-active': activeSearchItem === product }]"
        @click="add(product)">
        <oie-base-image
          :images="getAssetPath(productImage(product.image))"
          :alt-text="product.name"
          image-width="3.75rem"
          image-height="3.75rem"
          class="article-search-result__image"></oie-base-image>

        <div class="article-search-result__content">
          <div class="article-search-result__name">
            {{ product.name }}
          </div>
          <div class="article-search-result__sub-name">
            {{ product.id }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import gtm from '@/core/enhanced-ecommerce-gtm'
import { listTypes } from '@/core/analytics-variables'
import cartService from '@/core/cart-service'

export default {
  props: {
    name: {
      type: [String, Number],
      default: ''
    },
    useGrayInput: {
      type: Boolean,
      default: true
    },
    gaCategory: {
      type: String,
      default: null
    }
  },
  data: () => ({
    searchText: '',
    productQuantity: 1,
    searchResult: [],
    showSearchResults: false,
    selectedRowId: null,
    selectedRowProductId: null,
    activeSearchResultIndex: -1
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('myMachines', ['globalMachine', 'globalMachineOwner']),
    hasResults() {
      return this.searchResult.length > 0
    },
    activeSearchItem() {
      return this.searchResult[this.activeSearchResultIndex]
    }
  },
  watch: {
    searchText: _.debounce(function (query) {
      const performSearch = query.length > 2
      if (performSearch && !this.selectedRowId) {
        this.search()
      }

      if (!this.selectedRowId) {
        this.toggleSearchResult(performSearch)
      } else {
        this.clearAndCloseSearch()
      }
    }, 100),
    showSearchResults(value) {
      if(!value) {
        this.activeSearchResultIndex = -1
      }
    }
  },
  methods: {
    ...mapActions('commerce', ['getCart', 'addToCart']),
    toggleSearchResult(toggle) {
      this.showSearchResults = toggle
    },
    clearAndCloseSearch() {
      this.toggleSearchResult(false)
      this.searchResult = []
    },
    search() {
      return httpService.get('product/search', { query: this.searchText }).then(({ data }) => {
        this.searchResult = data.data
      })
    },
    clickedOutside() {
      this.clearAndCloseSearch()
    },
    add(product) {
      this.selectedRowId = product.id
      this.selectedRowProductId = product.productId
      this.searchText = product.name
    },
    async addProduct() {
      if (this.name === this.clientModel.cartId) {
        this.productQuantity = Math.round(this.productQuantity)

        const payload = { itemId: this.selectedRowId, productId: this.selectedRowProductId, quantity: this.productQuantity }
        this.addToCart(payload).then((data) => {
          if (this.enableGtm && data.cart) {
            const addedProduct = data.cart.rows.find(x => x.itemId.toLowerCase() === this.selectedRowId.toLowerCase())
            const gtmObject = gtm.createGtmItem(
              addedProduct.itemId,
              addedProduct.displayName,
              addedProduct.item ? addedProduct.item.purchasedToVehicle : '',
              addedProduct.item ? addedProduct.item.purchasedOnCategory : '',
              this.formatPriceWithoutCurrency(addedProduct.priceExVat),
              this.productQuantity,
              listTypes.CART)

            gtm.addToCart(gtmObject)
          }

          this.getCart(this.clientModel.cartType)

          this.resetAfterAddProduct()
        })
      } else {
        const previewRow = await cartService.previewRow({ itemId: this.selectedRowId, productId: this.selectedRowProductId, quantity: this.productQuantity, machineOwnerId: this.globalMachineOwner, machineId: this.globalMachine, cartId: this.name })
        if(previewRow) {
          this.publish(`add-preview-row`, { id: this.name, data: previewRow })
          this.resetAfterAddProduct()
        }
      }
    },
    resetAfterAddProduct() {
      this.selectedRowId = null
      this.selectedRowProductId = null
      this.productQuantity = 1
      this.searchText = ''
    },
    onSearchKeyDown(e) {
      switch (e.keyCode) {
        case 38:
          if (--this.activeSearchResultIndex < 0) {
            this.activeSearchResultIndex = this.searchResult.length - 1
          }
          break
        case 40:
          if (++this.activeSearchResultIndex === this.searchResult.length) {
            this.activeSearchResultIndex = 0
          }
          break
        case 13:
          if(this.activeSearchItem) {
            this.add(this.activeSearchItem)
          } else {
            const exactMatch = _.find(this.searchResult, i => i.id === this.searchText)
            if(exactMatch) {
              this.add(exactMatch)
            } else if (this.selectedRowId) {
              this.addProduct()
            }
          }
          break
        case 27:
          this.clearAndCloseSearch()
          this.searchText = ''
          break
        case 9:
          break
        default:
          this.toggleSearchResult(true)
          this.activeSearchResultIndex = -1
          this.selectedRowId = null
          this.selectedRowProductId = null
          break
      }
    },
    productImage(image) {
      if (image) { return image + '?preset=ProductThumb&watermark=OIEWatermarkSmall' }
      return '/globalassets/missing404/404.png?preset=ProductThumb'
    }
  }
}
</script>

<style lang="scss">
.article-search {
  position: relative;
  display: flex;
  align-items: flex-end;
  max-width: 35rem;
}

.article-search__amount {
  margin-left: measure(quarter);
  background-color: color(background-gray-5);
}

.article-search__search-action {
  @include reset-button;
  width: 1.25rem;
  height: 1.25rem;
}

.article-search__search-svg {
  width: 1.25rem;
  height: 1.25rem;
}

.article-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: color(background-white);
  border-radius: 0 0 other(border-radius) other(border-radius);
  box-shadow: 0 0 5px 0 rgba(119, 119, 119, .5);
  padding: 0 measure(half);

  @include media(small) {
    right: initial;
    width: 32.5rem;
  }
}

.article-search-results--has-hits {
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.article-search-result {
  @include body-smaller;
  position: relative;
  display: flex;
  align-content: center;
  border-bottom: 1px solid color(border-gray);
  padding: measure(half) measure(full) measure(half) 0;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    cursor: pointer;
  }
}

.article-search-result--is-active {
  background-color: color(background-blue-4);
}

.article-search-result__content {
  display: flex;
  flex-direction: column;
  margin-left: measure(half);
}

.article-search-result__image {
  width: 3.75rem;
  height: 3.75rem;
}

.article-search-result__name {
  @include body-smaller-bold;
  color: color(text-blue);
}

.article-search-result:hover .article-search-result__name,
.article-search-result--is-selected .article-search-result__name {
  text-decoration: underline;
}
</style>
